import { useCallback } from 'react';

import { EditorState, Modifier } from 'draft-js';
import { Mic } from 'lucide-react';

import { useVoiceToText } from '@/components/VoiceToText/hooks/useVoiceToText';
import { ConfirmTypes, useToastStore } from '@/store';

interface ToolbarVoiceButtonProps {
  editorState?: EditorState;
  onChange?: (editorState: EditorState) => void;
  isEditorFocused?: boolean;
}

const ToolbarVoiceButton = ({
  editorState,
  onChange,
  isEditorFocused = false,
}: ToolbarVoiceButtonProps) => {
  const { updateToast } = useToastStore();

  const handleTextOutput = useCallback(
    (transcript: string) => {
      if (!editorState || !onChange) return;

      const targetEditorState = isEditorFocused
        ? editorState
        : EditorState.moveFocusToEnd(editorState);
      const contentState = targetEditorState.getCurrentContent();
      const selectionState = targetEditorState.getSelection();

      const newContentState = Modifier.insertText(
        contentState,
        selectionState,
        transcript + ' ',
      );

      const newEditorState = EditorState.push(
        targetEditorState,
        newContentState,
        'insert-characters',
      );

      onChange(newEditorState);
    },
    [editorState, isEditorFocused, onChange],
  );

  const handleError = useCallback(
    (errMsg: string) => {
      updateToast({
        open: true,
        message: errMsg,
        type: ConfirmTypes.WARNING,
      });
    },
    [updateToast],
  );

  const { isTranscribing, isRecording, startRecording, stopRecording } =
    useVoiceToText({
      handleTextOutput,
      handleError,
    });

  const handleStartRecording = useCallback(() => {
    if (editorState && onChange && !isEditorFocused) {
      onChange(EditorState.moveFocusToEnd(editorState));
    }

    startRecording();
  }, [editorState, isEditorFocused, onChange, startRecording]);

  const handleClick = isRecording ? stopRecording : handleStartRecording;
  const title = isRecording ? 'Stop recording' : 'Voice to text';

  return (
    <div
      className={`rdw-option-wrapper ${
        isRecording
          ? '!bg-red-500 !border-red-500 animate-pulse'
          : isTranscribing
          ? '!opacity-50 pointer-events-none'
          : ''
      }`}
      onClick={handleClick}
      title={title}
    >
      {isTranscribing ? (
        <div className="animate-spin rounded-full w-[15px] h-[15px] border-2 border-gray-500 border-t-transparent" />
      ) : (
        <Mic size={20} />
      )}
    </div>
  );
};

export default ToolbarVoiceButton;
